/**
 * LeaderboardPage
 * 
 * Displays team rankings, achievements, and manager recognition features.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../components/auth/AuthContext';
import { useGamification } from '../components/gamification/GamificationContext';
import { GamificationStatsGrid } from '../components/gamification/GamificationProgressPanel';
import { 
  getLeaderboard, 
  awardBonusPoints,
  getUserLevel,
  getAchievementsFromFirestore 
} from '../utils/gamificationUtils';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { hasPermission, FEATURE_PERMISSIONS } from '../utils/permissions';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

const LeaderboardPage = () => {
  const { currentUser } = useAuth();
  const { profile: myProfile, currentLevel } = useGamification();
  const [leaderboard, setLeaderboard] = useState([]);
  const [users, setUsers] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('all');
  const [rewardModal, setRewardModal] = useState({ show: false, user: null });
  
  // Can award bonus points: must be able to modify data AND view all tasks (managers/admins)
  const isManager = hasPermission(currentUser, FEATURE_PERMISSIONS.TASK_VIEW_ALL);
  
  // Load users for name mapping
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersSnap = await getDocs(collection(db, collectionPath('users')));
        setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error('Failed to load users:', error);
      }
    };
    loadUsers();
  }, []);
  
  // Load achievements from Firestore
  useEffect(() => {
    const loadAchievements = async () => {
      try {
        const achData = await getAchievementsFromFirestore();
        // Only show enabled achievements
        setAchievements(achData.filter(a => a.isEnabled !== false));
      } catch (error) {
        console.error('Failed to load achievements:', error);
      }
    };
    loadAchievements();
  }, []);
  
  // Load leaderboard
  useEffect(() => {
    const loadLeaderboard = async () => {
      setLoading(true);
      try {
        const data = await getLeaderboard(timeframe, 50);
        setLeaderboard(data);
      } catch (error) {
        console.error('Failed to load leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };
    loadLeaderboard();
  }, [timeframe]);
  
  // Map user IDs to names
  const usersById = useMemo(() => 
    new Map(users.map(u => [u.id, u])), 
    [users]
  );

  // Set of user IDs hidden from reports/achievements
  const hiddenUserIds = useMemo(() => 
    new Set(users.filter(u => u.hideFromReports).map(u => u.id)),
    [users]
  );
  
  // Filter leaderboard to exclude hidden users
  const visibleLeaderboard = useMemo(() => 
    leaderboard.filter(entry => !hiddenUserIds.has(entry.userId)),
    [leaderboard, hiddenUserIds]
  );

  // Find my rank
  const myRank = useMemo(() => 
    visibleLeaderboard.findIndex(entry => entry.userId === currentUser?.uid) + 1,
    [visibleLeaderboard, currentUser?.uid]
  );
  
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🏆 Team Leaderboard
          </h1>
          <p className="text-gray-600">
            See how you stack up against the team
          </p>
        </div>
        
        {/* My Position Card */}
        {myProfile && (
          <div className="bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400 rounded-2xl p-6 mb-8 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-5xl animate-glow bg-white/20 rounded-full p-3">
                  {currentLevel?.icon}
                </div>
                <div>
                  <div className="text-sm opacity-90">Your Rank</div>
                  <div className="text-4xl font-bold">
                    {myRank > 0 ? `#${myRank}` : '—'}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm opacity-90">Total Points</div>
                <div className="text-4xl font-bold">
                  {myProfile.totalPoints?.toLocaleString() || 0}
                </div>
                <div className="text-sm opacity-90 mt-1">
                  Level {currentLevel?.level} • {currentLevel?.name}
                </div>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-4 mt-6 pt-4 border-t border-white/20">
              <QuickStat label="Deals" value={myProfile.stats?.dealsWon || 0} />
              <QuickStat label="Tasks" value={myProfile.stats?.tasksCompleted || 0} />
              <QuickStat label="Streak" value={`${myProfile.currentStreak || 0}🔥`} />
              <QuickStat label="Badges" value={myProfile.achievements?.length || 0} />
            </div>
          </div>
        )}
        
        {/* Timeframe Filter */}
        <div className="flex justify-center gap-2 mb-6">
          {['all', 'month', 'week'].map((tf) => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                timeframe === tf
                  ? 'bg-amber-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tf === 'all' ? 'All Time' : tf === 'month' ? 'This Month' : 'This Week'}
            </button>
          ))}
        </div>
        
        {/* Leaderboard Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-500">
              Loading leaderboard...
            </div>
          ) : visibleLeaderboard.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              No data yet. Start earning points!
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {visibleLeaderboard.map((entry, index) => {
                const user = usersById.get(entry.userId);
                const level = getUserLevel(entry.totalPoints || 0);
                const isMe = entry.userId === currentUser?.uid;
                
                return (
                  <div 
                    key={entry.id}
                    className={`flex items-center gap-4 p-4 transition-colors ${
                      isMe ? 'bg-amber-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Rank */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-700' :
                      index === 2 ? 'bg-amber-600 text-amber-100' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    
                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{level.icon}</span>
                        <span className="font-semibold text-gray-900 truncate">
                          {user?.displayName || 'Unknown User'}
                          {isMe && <span className="text-amber-600 ml-1">(You)</span>}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        Level {level.level} • {level.name}
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="hidden sm:flex items-center gap-4 text-sm text-gray-500">
                      <span title="Deals Won">💰 {entry.stats?.dealsWon || 0}</span>
                      <span title="Tasks">✅ {entry.stats?.tasksCompleted || 0}</span>
                      <span title="Streak">🔥 {entry.currentStreak || 0}</span>
                    </div>
                    
                    {/* Points */}
                    <div className="text-right">
                      <div className="font-bold text-lg text-gray-900">
                        {entry.totalPoints?.toLocaleString() || 0}
                      </div>
                      <div className="text-xs text-gray-500">points</div>
                    </div>
                    
                    {/* Manager Reward Button */}
                    {isManager && !isMe && (
                      <button
                        onClick={() => setRewardModal({ show: true, user: { ...entry, displayName: user?.displayName } })}
                        className="p-2 text-amber-600 hover:bg-amber-100 rounded-full transition-colors"
                        title="Award bonus points"
                      >
                        🎁
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* My Stats Section - Same as Progress Widget */}
        {myProfile && (
          <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              📊 Your Stats
            </h2>
            <GamificationStatsGrid stats={myProfile.stats} variant="grid" />
          </div>
        )}
        
        {/* Achievement Showcase */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            🏅 Available Achievements ({achievements.length})
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {achievements.map((achievement) => {
              const earned = myProfile?.achievements?.includes(achievement.id);
              return (
                <div 
                  key={achievement.id}
                  className={`p-4 rounded-xl text-center transition-all ${
                    earned 
                      ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-300' 
                      : 'bg-gray-100 opacity-60'
                  }`}
                  title={achievement.description}
                >
                  <div className={`text-4xl mb-2 ${!earned && 'grayscale'}`}>
                    {achievement.icon}
                  </div>
                  <div className={`text-sm font-medium ${earned ? 'text-gray-800' : 'text-gray-500'}`}>
                    {achievement.name}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-1 line-clamp-2" title={achievement.description}>
                    {achievement.description}
                  </div>
                  <div className="text-xs text-amber-600 mt-1">
                    +{achievement.points} pts
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Manager Reward Modal */}
      {rewardModal.show && (
        <RewardModal 
          user={rewardModal.user}
          managerId={currentUser?.uid}
          onClose={() => setRewardModal({ show: false, user: null })}
          onSuccess={() => {
            setRewardModal({ show: false, user: null });
            // Refresh leaderboard
            getLeaderboard(timeframe, 50).then(setLeaderboard);
          }}
        />
      )}
    </div>
  );
};

const QuickStat = ({ label, value }) => (
  <div className="text-center">
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-xs opacity-80">{label}</div>
  </div>
);

const RewardModal = ({ user, managerId, onClose, onSuccess }) => {
  const [points, setPoints] = useState(50);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const presetReasons = [
    'Outstanding customer service',
    'Exceeded sales target',
    'Great teamwork',
    'Creative problem solving',
    'Going above and beyond',
    'Helpful to colleagues'
  ];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Please provide a reason');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      await awardBonusPoints(managerId, user.userId, points, reason);
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-scale-up">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">
              🎁 Reward {user.displayName}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              ✕
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Points Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Points to Award
              </label>
              <div className="flex gap-2 flex-wrap">
                {[25, 50, 100, 250, 500].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPoints(p)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      points === p
                        ? 'bg-amber-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    +{p}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Quick Reasons */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quick Reasons
              </label>
              <div className="flex flex-wrap gap-2">
                {presetReasons.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setReason(r)}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      reason === r
                        ? 'bg-amber-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Custom Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Award *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                rows={2}
                placeholder="Why are you recognizing this person?"
              />
            </div>
            
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
            
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600 disabled:opacity-50"
              >
                {saving ? 'Awarding...' : `Award +${points} Points`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
